/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useMemo } from 'react';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { SaleDetailDiscount, SaleDetailItem } from '../domain/salesTypes';
import { formatSaleDetailLinePromoHint, summarizeSaleDetailLines } from '../application/saleDetailPromotionPresentation';
import { formatMoney as formatPromoMoney } from '../../shared/format/public-api';
import { saleDetailStyles as styles } from './saleDetail.styles';
import { formatQty } from '@shared/quantity';
import { ticketColors } from '../../shared/theme/ticketColors';

type Props = {
  items: SaleDetailItem[];
  discounts: SaleDetailDiscount[];
  /** Mostrar atribución de empleados (requiere capability commissions) */
  showAttribution?: boolean;
};

function formatItemOptions(item: SaleDetailItem): string | null {
  const opts = item.options;
  if (!opts?.length) return null;
  const label = opts
    .map((o) => {
      const row = o as { choiceName?: string; choice_name?: string; qty?: number };
      const name = String(row.choiceName ?? row.choice_name ?? '').trim();
      const qty = Math.max(1, Number(row.qty) || 1);
      if (!name) return '';
      return qty > 1 ? `${name}×${qty}` : name;
    })
    .filter(Boolean)
    .join(', ');
  return label || null;
}

export function SaleDetailItemsBreakdown({ items, discounts, showAttribution = false }: Props) {
  const { width } = useWindowDimensions();
  const layoutWide = width >= 768;
  const rows = useMemo(() => summarizeSaleDetailLines(items), [items]);

  if (items.length === 0) {
    return <Text style={styles.caption}>Sin ítems.</Text>;
  }

  const renderAttribution = (item: SaleDetailItem) => {
    if (!showAttribution) return null;
    const parts: string[] = [];
    if (item.performedByDisplayName) parts.push(`Realiza: ${item.performedByDisplayName}`);
    if (item.soldByDisplayName) parts.push(`Vende: ${item.soldByDisplayName}`);
    if (parts.length === 0) return null;
    return (
      <View style={attributionStyles.row}>
        <Ionicons name="person-outline" size={11} color={ticketColors.ink3} />
        <Text style={attributionStyles.text} numberOfLines={1}>
          {parts.join(' · ')}
        </Text>
      </View>
    );
  };

  if (layoutWide) {
    return (
      <View style={styles.checkoutDetailTableWrap}>
        <View style={styles.checkoutDetailTableHeader}>
          <Text style={[styles.checkoutDetailCell, styles.checkoutDetailCellProduct]}>Ítem</Text>
          <Text style={[styles.checkoutDetailCell, styles.checkoutDetailCellQty]}>Cantidad</Text>
          <Text style={[styles.checkoutDetailCell, styles.checkoutDetailCellMoney]}>Lista</Text>
          <Text style={[styles.checkoutDetailCell, styles.checkoutDetailCellMoney]}>Descuento</Text>
          <Text style={[styles.checkoutDetailCell, styles.checkoutDetailCellMoney]}>Neto</Text>
        </View>
        {items.map((item, index) => {
          const row = rows.find((value) => value.lineId === item.id);
          if (!row) return null;
          const lineHint = formatSaleDetailLinePromoHint(item, discounts);
          const optionsLabel = formatItemOptions(item);
          return (
            <View
              key={item.id}
              style={[
                styles.checkoutDetailTableRow,
                index === items.length - 1 ? styles.checkoutDetailTableRowLast : null,
              ]}
            >
              <View style={styles.checkoutDetailCellProduct}>
                <Text style={styles.saleDetailItemName} numberOfLines={2}>
                  {item.label ?? item.productId}
                </Text>
                {optionsLabel ? (
                  <Text style={styles.saleDetailLinePromoHint} numberOfLines={2}>
                    {optionsLabel}
                  </Text>
                ) : null}
                {item.lineNote?.trim() ? (
                  <Text style={styles.saleDetailLinePromoHint} numberOfLines={2}>
                    Nota: {item.lineNote.trim()}
                  </Text>
                ) : null}
                {lineHint ? (
                  <Text style={styles.saleDetailLinePromoHint} numberOfLines={1}>
                    {lineHint}
                  </Text>
                ) : null}
                {renderAttribution(item)}
              </View>
              <Text style={[styles.checkoutDetailCell, styles.checkoutDetailCellQty]}>
                {formatQty(row.quantity, item.unitOfMeasure)}
              </Text>
              <Text style={[styles.checkoutDetailCell, styles.checkoutDetailCellMoney]}>
                {formatPromoMoney(row.listTotal)}
              </Text>
              <Text
                style={[
                  styles.checkoutDetailCell,
                  styles.checkoutDetailCellMoney,
                  row.discountTotal > 0 ? styles.checkoutDetailDiscount : null,
                ]}
              >
                {row.discountTotal > 0 ? `−${formatPromoMoney(row.discountTotal)}` : '—'}
              </Text>
              <Text style={[styles.checkoutDetailCell, styles.checkoutDetailCellMoney, styles.checkoutDetailNet]}>
                {formatPromoMoney(row.netTotal)}
              </Text>
            </View>
          );
        })}
      </View>
    );
  }

  return (
    <View style={styles.saleDetailItemsMobileWrap}>
      {items.map((item, index) => {
        const row = rows.find((value) => value.lineId === item.id);
        if (!row) return null;
        const lineHint = formatSaleDetailLinePromoHint(item, discounts);
        const optionsLabel = formatItemOptions(item);
        return (
          <View
            key={item.id}
            style={[
              styles.saleDetailItemRow,
              index === items.length - 1 ? styles.saleDetailItemRowLast : null,
            ]}
          >
            <View style={styles.saleDetailItemMain}>
              <Text style={styles.saleDetailItemName}>
                {item.label ?? item.productId}
                {row.quantity !== 1 ? ` × ${formatQty(row.quantity, item.unitOfMeasure)}` : ''}
              </Text>
              {optionsLabel ? <Text style={styles.saleDetailItemCode}>{optionsLabel}</Text> : null}
              {item.lineNote?.trim() ? (
                <Text style={styles.saleDetailItemCode}>Nota: {item.lineNote.trim()}</Text>
              ) : null}
              {item.sku ? <Text style={styles.saleDetailItemCode}>Código: {item.sku}</Text> : null}
              {lineHint ? (
                <Text style={styles.saleDetailLinePromoHint} numberOfLines={1}>
                  {lineHint}
                </Text>
              ) : null}
              {renderAttribution(item)}
            </View>
            <View style={styles.saleDetailItemAmountCol}>
              {row.discountTotal > 0 ? (
                <Text style={styles.saleDetailItemAmountList}>{formatPromoMoney(row.listTotal)}</Text>
              ) : null}
              <Text style={[styles.saleDetailItemAmount, row.discountTotal > 0 ? styles.saleDetailItemAmountNet : null]}>
                {formatPromoMoney(row.netTotal)}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}

const attributionStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 3,
  },
  text: {
    fontSize: 11,
    fontWeight: '500',
    color: ticketColors.ink3,
    flexShrink: 1,
  },
});
